import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import {
  AuthRepository,
  getStoredToken,
  getUser,
} from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { LocalStorageService } from './local-storage-service';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from 'environments/environment';
import { NotificationsService } from 'app/state/notifications.service';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { CoachMenteeChatService } from './coachMenteeChat.service';
import { GamificationService } from './gamification.service';
import {
  AchievementUnlock,
  ChallengeComplete,
  GamificationUpdate,
} from '../models/gamification/gamification.dtos';

@Injectable({ providedIn: 'root' })
export class TenantSyncService {
  private connection: signalR.HubConnection;

  public connect(): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    return this.connection.start().catch((err) => {
      console.error('SignalR connection failed:', err);
      throw err;
    });
  }

  constructor(
    private auth: AuthService,
    public generalDataRepo: GeneralDataRepository,
    public router: Router,
    private storage: LocalStorageService,
    private authRepo: AuthRepository,
    private notificationsService: NotificationsService,
    private notificationsRepo: NotificationsRepository,
    private translocoService: TranslocoService,
    private chatService: CoachMenteeChatService,
    private gamificationService: GamificationService,
  ) {
    const user = getStoredToken();

    let url = '/tenantHub';
    if (environment.production) {
      url = '/core/tenantHub';
    }
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => user ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('TenantChanged', (newTenantId: string) => {
      const currentTenantId = localStorage.getItem('activeTenantId');
      if (currentTenantId === newTenantId) {
        return;
      }

      const token = getStoredToken();
      if (!token) {
        console.error('Cannot switch tenant: no auth token available');
        return;
      }
      // Switch server-side first; only mirror into client state on success so
      // a failed request can't desync the UI onto a tenant the backend isn't
      // scoped to (which shows the previous tenant's data).
      this.auth.setActiveTenant(token, newTenantId).subscribe({
        next: () => {
          if (newTenantId) {
            localStorage.setItem('activeTenantId', newTenantId);
          } else {
            localStorage.removeItem('activeTenantId');
          }
          // Keep both storage mechanisms in lockstep and written before
          // navigation, so a navigation hang can't leave localStorage and the
          // tenant store diverged.
          storage.setTenantId(newTenantId);
          this.generalDataRepo.updateAllGeneralData(true);
          this.router.navigate(['/home']);
        },
        error: (err) => {
          // Background sync pushed from the server (another tab/device switched
          // tenant) — the user took no action here, so log only. No toast: a
          // "please try again" prompt would be misleading with nothing to retry.
          console.error('Failed to switch tenant', err);
        },
      });
    });

    this.connection.on(
      'ImpersonateStarted',
      (newUserId: string, newTenantId: string) => {
        const user = getUser();
        if (user?.id === newUserId) {
          return;
        }

        this.auth.impersonate(newUserId!, newTenantId).subscribe({
          next: (responce) => {
            if (responce.user) {
              localStorage.setItem('isImpersonatingMode', 'true');
              this.authRepo.setUser(responce.user);
              this.translocoService.setActiveLang(
                responce?.user?.language ?? 'en',
              );
              if (newTenantId) {
                localStorage.setItem('activeTenantId', newTenantId);
              } else {
                localStorage.removeItem('activeTenantId');
              }
            }
            this.authRepo.setToken(responce.token);
            window.location.reload();
          },
        });
      },
    );

    this.connection.on('ImpersonateStoped', (resp: any) => {
      this.auth.unimpersonateWithoutRequest(resp);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    });

    this.connection.on('ReloadNotifications', (resp: any) => {
      this.notificationsService.loadAllCount().subscribe((x) => {
        this.notificationsRepo.notificationCount$.next(x);
      });
    });

    this.connection.on('ReloadMessages', (resp: any) => {
      this.chatService.getAllMessagesForUser().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateMessages(allMessages);
      });
    });
    this.connection.on('ReloadGroupMessages', (resp: any) => {
      this.chatService.getAllMessagesForUserGroup().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateGroupMessages(allMessages);
      });
    });
    this.connection.on('ReloadSidebar', (resp: any) => {
      this.generalDataRepo.updateAllGeneralData(true);
    });
    this.connection.on('GamificationUpdated', (update: GamificationUpdate) => {
      this.gamificationService.updates$.next(update);
    });
    this.connection.on('AchievementUnlocked', (unlock: AchievementUnlock) => {
      this.gamificationService.achievementUnlocked$.next(unlock);
    });
    this.connection.on('ChallengeCompleted', (complete: ChallengeComplete) => {
      this.gamificationService.challengeCompleted$.next(complete);
    });
    this.connection.on('LogOut', (resp: any) => {
      localStorage.clear();
      localStorage.setItem('isImpersonatingMode', 'false');
      this.router.navigate(['/signin']);
      // this.generalDataRepo.updateAllGeneralData(true);
    });

    this.connection
      .start()
      .catch((err) => console.error('SignalR error:', err));
  }
}
